import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";
import { usePortal } from "../context/PortalContext";
import { useAuth } from "../context/AuthContext";
import AuthDialog from "./AuthDialog";

export default function Nav() {
  const { t, lang, toggleLang, isRtl } = useLanguage();
  const { count, openCart } = useCart();
  const { go } = usePortal();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = `text-xs uppercase tracking-[0.22em] transition-colors ${
    scrolled ? "text-xir-dark/70 hover:text-xir-dark" : "text-xir-paper/70 hover:text-xir-paper"
  }`;
  const iconCls = `transition-colors ${scrolled ? "text-xir-dark/70 hover:text-xir-dark" : "text-xir-paper/70 hover:text-xir-paper"}`;

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "border-b hairline bg-xir-paper/85 backdrop-blur-md" : "border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-10">
          <button
            onClick={() => go("/")}
            data-testid="nav-wordmark"
            className={`font-display text-xl font-semibold tracking-[0.08em] transition-colors sm:text-2xl ${
              scrolled ? "text-xir-dark" : "text-xir-paper"
            }`}
          >
            CAFÉXIR
          </button>

          <div className="hidden items-center gap-10 md:flex">
            <button onClick={() => go("/shop")} data-testid="nav-shop-link" className={linkCls}>
              {t("nav.shop")}
            </button>
            <button onClick={() => go("/cupprint")} data-testid="nav-cupprint-link" className={linkCls}>
              {t("nav2.cupprint")}
            </button>
            <button onClick={() => go("/story")} data-testid="nav-story-link" className={linkCls}>
              {t("nav.story")}
            </button>
            <button
              onClick={() => {
                const scroll = () => document.getElementById("how-to-order")?.scrollIntoView({ behavior: "smooth" });
                if (location.pathname === "/") scroll();
                else { go("/"); setTimeout(scroll, 1600); }
              }}
              data-testid="nav-howto-link"
              className={linkCls}
            >
              {t("nav.howto")}
            </button>
          </div>

          <div className="flex items-center gap-5 sm:gap-7">
            {user ? (
              <button
                onClick={() => go("/account")}
                data-testid="account-button"
                title={`${user.name} — ${t("account.title")}`}
                aria-label={t("account.title")}
                className="flex items-center"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    referrerPolicy="no-referrer"
                    className="h-7 w-7 rounded-full border border-xir-gold/40 object-cover"
                    data-testid="user-avatar"
                  />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-xir-gold/40 text-[0.6rem] font-semibold text-xir-gold" data-testid="user-avatar">
                    {(user.name || user.email || "C")[0].toUpperCase()}
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={() => setAuthOpen(true)}
                data-testid="sign-in-button"
                aria-label={t("auth.signIn")}
                title={t("auth.signIn")}
                className={iconCls}
              >
                <User size={18} strokeWidth={1.5} />
              </button>
            )}
            <button
              onClick={toggleLang}
              data-testid="language-switcher"
              className={`text-xs tracking-[0.18em] transition-colors hover:text-xir-gold ${
                scrolled ? "text-xir-dark/70" : "text-xir-paper/70"
              }`}
              aria-label="Switch language"
            >
              {lang === "en" ? "العربية" : "EN"}
            </button>
            <button onClick={openCart} data-testid="cart-button" className={`relative ${iconCls}`} aria-label={t("nav.cart")}>
              {/* the cart is a small cup that fills with coffee */}
              <span
                className={`relative flex h-7 w-6 items-end overflow-hidden rounded-b-md border transition-colors ${
                  scrolled ? "border-xir-dark/40" : "border-xir-paper/40"
                }`}
              >
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0"
                  style={{
                    background: "linear-gradient(to top, #170d07, #2a1a0e)",
                    borderTop: "1px solid rgba(182,154,90,0.75)",
                  }}
                  initial={false}
                  animate={{ height: `${Math.min(count / 6, 1) * 100}%` }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                />
              </span>
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0.3 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 14 }}
                  data-testid="cart-count"
                  className="absolute -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-xir-gold text-[0.6rem] font-semibold text-xir-dark ltr:-right-2 rtl:-left-2"
                >
                  {count}
                </motion.span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-button"
              className={`${iconCls} md:hidden`}
              aria-label={t("nav.menu")}
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] flex flex-col bg-xir-dark"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="font-display text-xl font-semibold tracking-[0.08em] text-xir-paper">CAFÉXIR</span>
              <button
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-menu-close"
                aria-label={t("nav.close")}
                className="text-xir-paper/60 hover:text-xir-paper"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
              {[
                { to: "/shop", label: t("nav.shop"), id: "mobile-nav-shop" },
                { to: "/cupprint", label: t("nav2.cupprint"), id: "mobile-nav-cupprint" },
                { to: "/story", label: t("nav.story"), id: "mobile-nav-story" },
                { to: "/#how-to-order", label: t("nav.howto"), id: "mobile-nav-howto" },
              ].map((item, i) => (
                <div key={item.to} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        if (item.to === "/#how-to-order") {
                          const scroll = () => document.getElementById("how-to-order")?.scrollIntoView({ behavior: "smooth" });
                          if (location.pathname === "/") scroll();
                          else { go("/"); setTimeout(scroll, 1600); }
                        } else {
                          go(item.to);
                        }
                      }}
                      data-testid={item.id}
                      className="font-display block py-3 text-5xl font-medium text-xir-paper"
                    >
                      {item.label}
                    </button>
                  </motion.div>
                </div>
              ))}
            </nav>
            <p className="px-8 pb-10 text-xs tracking-[0.25em] text-xir-paper/40">
              {isRtl ? "السحر في كل كوب" : "MAGIC IN EVERY CUP"}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}
